import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import deLocale from 'date-fns/locale/de';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { jhTheme } from '../../../themeProvider';
import { InternalInvoice } from '../../BaseController/types/internalContract';
import { CustomButton } from '../../common/button/customButton';

export interface InvoiceDataProps {
  invoiceData: InternalInvoice[];
}

export const InvoiceData = ({ invoiceData }: InvoiceDataProps) => {
  const { t } = useTranslation();

  const date = new Date();

  const [invoiceDateBegin, setInvoiceDateBegin] = useState(date);
  const [invoiceDateEnd, setInvoiceDateEnd] = useState(date);
  const [invoiceDataInternal, setinvoiceDataInternal] = useState(invoiceData);

  const onFilter = (begin: Date, end: Date) => {
    setinvoiceDataInternal(
      invoiceData.filter((invoice) => {
        return (
          new Date(invoice.billingDateExternal) > begin &&
          new Date(invoice.billingDateExternal) < end
        );
      }),
    );
  };
  const setInvoiceDateStartDefault = () => {
    let highestBillingDate = new Date();
    if (invoiceData.length > 0) {
      highestBillingDate = new Date(
        invoiceData[invoiceData.length - 1].billingDateExternal,
      );
    }

    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    let invoiceDateBeginTmp: Date;

    if (highestBillingDate && highestBillingDate < d) {
      invoiceDateBeginTmp = new Date(
        highestBillingDate.setDate(highestBillingDate.getDate() - 1),
      );
    } else {
      invoiceDateBeginTmp = d;
    }
    setInvoiceDateBegin(invoiceDateBeginTmp);
    return invoiceDateBeginTmp;
  };

  const setInvoiceDateEndDefault = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    setInvoiceDateEnd(d);
    return d;
  };

  const handleInvoiceDateBeginChange = (tmpDate: Date) => {
    if (tmpDate === date) {
      setInvoiceDateStartDefault();
    } else {
      setInvoiceDateBegin(new Date(tmpDate.toISOString()));
      onFilter(tmpDate, invoiceDateEnd);
    }
  };

  const handleInvoiceDateEndChange = (tmpDate: Date) => {
    if (tmpDate === date) {
      setInvoiceDateEndDefault();
    } else {
      setInvoiceDateEnd(new Date(tmpDate.toISOString()));
      onFilter(invoiceDateBegin, tmpDate);
    }
  };

  const resetFilter = () => {
    const begin = setInvoiceDateStartDefault();
    const end = setInvoiceDateEndDefault();
    onFilter(begin, end);
  };

  const showAll = () => {
    const lowestBillingDate = new Date(invoiceData[0].billingDateExternal);

    setInvoiceDateBegin(
      new Date(lowestBillingDate.setDate(lowestBillingDate.getDate() - 1)),
    );

    onFilter(
      new Date(lowestBillingDate.setDate(lowestBillingDate.getDate() - 1)),
      invoiceDateEnd,
    );
  };

  useEffect(() => {
    resetFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack direction="column" spacing={3} sx={{ width: '100%' }}>
      <Stack>
        <Typography variant="headlineDetailSection">
          {t('Invoices')} ({invoiceDataInternal.length})
        </Typography>
      </Stack>
      <Stack direction="row" spacing={2}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={deLocale}
        >
          <DatePicker
            slotProps={{
              textField: {
                className: 'TextField-without-border-radius',
                size: 'small',
                sx: { backgroundColor: jhTheme.palette.background.paper },
              },
            }}
            onChange={(value) => {
              if (value) {
                handleInvoiceDateBeginChange(value);
              }
            }}
            value={invoiceDateBegin}
            format={t('DateFormatShort') || ''}
          />

          <DatePicker
            slotProps={{
              textField: {
                className: 'TextField-without-border-radius',
                size: 'small',
                sx: { backgroundColor: jhTheme.palette.background.paper },
              },
            }}
            onChange={(value) => {
              if (value) {
                handleInvoiceDateEndChange(value);
              }
            }}
            value={invoiceDateEnd}
            format={t('DateFormatShort') || ''}
          />

          <CustomButton
            handleOnClick={() => {
              resetFilter();
            }}
            text={t('DefaultFilter')}
            sx={{
              fontFamily: 'JHFontBold',
              border: '1px solid transparent',
            }}
            color="primary"
            size="small"
            endIcon={<FilterAltOffIcon />}
          />

          <CustomButton
            handleOnClick={() => {
              showAll();
            }}
            text={t('ShowAll')}
            sx={{
              fontFamily: 'JHFontBold',
              border: '1px solid transparent',
            }}
            color="secondary"
            size="small"
            disabled={!(invoiceData.length > 0)}
            endIcon={<VisibilityIcon />}
          />
        </LocalizationProvider>
      </Stack>
      <TableContainer component={Box}>
        <Table
          sx={{
            minWidth: 650,
            backgroundColor: jhTheme.palette.background.paper,
          }}
        >
          <TableHead
            className="table-header"
            sx={{
              textTransform: 'uppercase',
              fontFamily: 'JHFontBold,arial,sans-serif',
            }}
          >
            <TableRow>
              <TableCell align="justify">
                <Typography variant="h4">{t('InvoiceNumber')}</Typography>
              </TableCell>
              <TableCell align="justify">
                <Typography variant="h4">{t('InvoiceType')}</Typography>
              </TableCell>
              <TableCell align="justify">
                <Typography variant="h4">{t('InvoiceDate')}</Typography>
              </TableCell>
              <TableCell align="justify">
                <Typography variant="h4">{t('Start')}</Typography>
              </TableCell>
              <TableCell align="justify">
                <Typography variant="h4">{t('End')}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="h4">{t('InvoiceAmount')}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoiceDataInternal.length > 0 ? (
              invoiceDataInternal.map((invoice: InternalInvoice) => (
                <TableRow key={invoice.invoiceNumber}>
                  <TableCell align="justify">
                    <Typography variant="body2">
                      {invoice.invoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell align="justify">
                    <Typography variant="body2">
                      {invoice.invoiceDescription}
                    </Typography>
                  </TableCell>
                  <TableCell align="justify">
                    <Typography variant="body2">
                      {invoice.billingDate}
                    </Typography>
                  </TableCell>
                  <TableCell align="justify">
                    <Typography variant="body2">{invoice.startDate}</Typography>
                  </TableCell>
                  <TableCell align="justify">
                    <Typography variant="body2">{invoice.endDate}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{invoice.netValue}</Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={6}>
                  <Typography variant="body1">{t('NoEntries')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
